/**
 * 密码 ECC 加密模块
 *
 * 方案：EC P-256 密钥对 + ECDH 派生 + HKDF 混合盐 + AES-GCM
 * 全部基于 Cloudflare Workers Web Crypto API 原生实现，无外部依赖。
 *
 * 注意：Web Crypto 要求密钥导入算法名必须与用法匹配。
 *   - 用 deriveBits 必须以 `{name:'ECDH', namedCurve:'P-256'}` 导入
 *   - 同一对 P-256 密钥既可作为 ECDH 也可作为 ECDSA，
 *     但 importKey 时只能指定一个算法。
 *   - 本模块统一以 ECDH 导入主密钥对用于派生。
 *
 * 流程：
 * 1. 生成随机 salt(16B) + iv(12B)
 * 2. ECDH(服务端私钥, 服务端公钥) → derivedBits(256)
 * 3. HKDF(derivedBits, salt, username) → keyMaterial(256)
 * 4. importKey(keyMaterial) → AES-GCM 256 key
 * 5. AES-GCM.encrypt(key, iv, password) → ciphertext
 * 6. 存储 base64(ciphertext), hex(salt), hex(iv)
 *
 * 解密反向流程，最终比对 plaintext === 输入密码。
 */

/** JWK 格式的 EC P-256 密钥 */
export interface ECJwk {
  kty: 'EC';
  crv: 'P-256';
  x: string;
  y: string;
  d?: string; // 仅私钥
  ext: boolean;
  key_ops?: string[];
}

export interface EncryptedPassword {
  /** AES-GCM 密文（base64） */
  cipher: string;
  /** 每用户随机盐（hex，16 字节） */
  salt: string;
  /** AES-GCM IV（hex，12 字节） */
  iv: string;
}

/** 编码工具 */
const encoder = new TextEncoder();

/** hex → Uint8Array */
function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
}

/** Uint8Array → hex */
function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** ArrayBuffer → base64 */
function bufToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/** base64 → Uint8Array */
function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * 从 JWK 字符串加载 EC P-256 私钥（用于 ECDH 派生）
 *
 * 注意：必须以 ECDH 算法名导入，否则 Web Crypto 拒绝 deriveBits。
 */
async function loadEcPrivateKey(jwkStr: string): Promise<CryptoKey> {
  const jwk = JSON.parse(jwkStr) as ECJwk;
  // 移除可能存在的 key_ops 限制（来自 ECDSA 生成的 JWK 可能带 sign 用法）
  const { key_ops, ...cleanJwk } = jwk;
  void key_ops;
  return crypto.subtle.importKey(
    'jwk',
    cleanJwk as JsonWebKey,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits'],
  );
}

/**
 * 从 JWK 字符串加载 EC P-256 公钥（用于 ECDH 派生）
 */
async function loadEcPublicKey(jwk: ECJwk): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'jwk',
    jwk as JsonWebKey,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );
}

/**
 * ECDH 派生共享密钥
 *
 * 用服务端主私钥与公钥协商出 256 位共享密钥材料。
 */
async function deriveBitsEcdh(
  privateKey: CryptoKey,
  publicKeyJwk: ECJwk,
): Promise<ArrayBuffer> {
  const ecdhPub = await loadEcPublicKey(publicKeyJwk);
  // workers-types 中 EcKeyDeriveParams 类型缺失，使用类型断言绕过
  return crypto.subtle.deriveBits(
    { name: 'ECDH', public: ecdhPub } as unknown as Parameters<
      typeof crypto.subtle.deriveBits
    >[0],
    privateKey,
    256,
  );
}

/**
 * HKDF 派生 AES 密钥材料
 *
 * @param ikm 原始密钥材料（ECDH derivedBits）
 * @param salt 每用户随机盐
 * @param info info 字符串（用 username 增强差异化）
 */
async function hkdfDerive(
  ikm: ArrayBuffer,
  salt: Uint8Array,
  info: string,
): Promise<ArrayBuffer> {
  const baseKey = await crypto.subtle.importKey('raw', ikm, 'HKDF', false, ['deriveBits']);
  return crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: salt as BufferSource,
      info: encoder.encode(info) as BufferSource,
    },
    baseKey,
    256,
  );
}

/** 将 256 位 raw bytes import 为 AES-GCM key */
async function importAesKey(raw: ArrayBuffer): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
}

/**
 * 加密密码
 *
 * @param password 明文密码
 * @param username 用户名（作为 HKDF info，增强差异化）
 * @param privateKeyJwk 服务端主私钥 JWK JSON 字符串（Workers Secret）
 * @param publicKeyJwk 服务端主公钥 JWK 对象
 */
export async function encryptPassword(
  password: string,
  username: string,
  privateKeyJwkStr: string,
  publicKeyJwk: ECJwk,
): Promise<EncryptedPassword> {
  // 1. 生成随机 salt 与 iv
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // 2. 加载私钥（以 ECDH 算法名导入）
  const privateKey = await loadEcPrivateKey(privateKeyJwkStr);

  // 3. ECDH 派生
  const ikm = await deriveBitsEcdh(privateKey, publicKeyJwk);

  // 4. HKDF 混入 salt + username → AES key
  const keyMaterial = await hkdfDerive(ikm, salt, `password:${username}`);
  const aesKey = await importAesKey(keyMaterial);

  // 5. AES-GCM 加密
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    aesKey,
    encoder.encode(password) as BufferSource,
  );

  return {
    cipher: bufToBase64(ciphertext),
    salt: bytesToHex(salt),
    iv: bytesToHex(iv),
  };
}

/**
 * 校验密码
 *
 * @param password 用户输入的明文密码
 * @param username 用户名
 * @param stored 已存储的加密密码结构
 * @param privateKeyJwkStr 服务端主私钥 JWK JSON 字符串
 * @param publicKeyJwk 服务端主公钥 JWK 对象
 * @returns true 表示密码匹配
 */
export async function verifyPassword(
  password: string,
  username: string,
  stored: EncryptedPassword,
  privateKeyJwkStr: string,
  publicKeyJwk: ECJwk,
): Promise<boolean> {
  try {
    const salt = hexToBytes(stored.salt);
    const iv = hexToBytes(stored.iv);

    const privateKey = await loadEcPrivateKey(privateKeyJwkStr);
    const ikm = await deriveBitsEcdh(privateKey, publicKeyJwk);
    const keyMaterial = await hkdfDerive(ikm, salt, `password:${username}`);
    const aesKey = await importAesKey(keyMaterial);

    // 解密失败会抛错（认证标签不匹配）
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      aesKey,
      base64ToBytes(stored.cipher) as BufferSource,
    );

    // 常量时间比对
    const expected = encoder.encode(password);
    const actual = new Uint8Array(plaintext);
    return constantTimeEqual(expected, actual);
  } catch {
    return false;
  }
}

/** 常量时间字节比对，防侧信道 */
function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a[i] ^ b[i];
  }
  return diff === 0;
}

/**
 * 解析 ECC 公钥 JWK 字符串
 */
export function parsePublicJwk(jwkStr: string): ECJwk {
  return JSON.parse(jwkStr) as ECJwk;
}
